import { useState } from 'react';
import {
  Grid,
  InputAdornment,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Link,
  Stack,
} from '@mui/material';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import { useNavigate } from 'react-router-dom';
import { forgotPassword } from '@/services/authApi';

export const ForgotPasswordPage = () => {
  const brandLogoSrc = '/asneris.png';
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const result = await forgotPassword(email);
      setSuccess(result.message || 'If the email exists, a temporary password has been sent.');
    } catch (err: unknown) {
      // Always show generic success to prevent email enumeration
      setSuccess('If the email exists, a temporary password has been sent.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        background: 'radial-gradient(circle at top right, rgba(78, 184, 197, 0.10), transparent 35%), #f6f8fc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 2.5, sm: 3 },
        py: { xs: 2.5, md: 3 },
      }}
    >
      <Grid
        container
        sx={{
          width: '100%',
          maxWidth: 1220,
          mx: 'auto',
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 24px 48px rgba(7, 32, 76, 0.10)',
        }}
      >
        <Grid
          item
          md={5}
          lg={5}
          sx={{
            display: { xs: 'none', md: 'block' },
            background: 'linear-gradient(145deg, #06295f 0%, #164e91 55%, #1c73ba 100%)',
          }}
        >
          <Box sx={{ height: '100%', p: { md: 3, lg: 3.5 }, color: '#e8f2ff', display: 'flex', flexDirection: 'column' }}>
            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 2.8 }}>
              <Box
                component="img"
                src={brandLogoSrc}
                alt="Asneris"
                sx={{ width: 30, height: 30, objectFit: 'contain' }}
              />
              <Stack spacing={0.1}>
                <Typography sx={{ color: '#ffffff', fontWeight: 800, lineHeight: 1 }}>Asneris</Typography>
                <Typography variant="caption" sx={{ color: '#b9d8fb', letterSpacing: '0.1em', fontWeight: 700 }}>
                  SEO / GEO Engine
                </Typography>
              </Stack>
            </Stack>

            <Typography sx={{ fontSize: { md: 28, lg: 32 }, fontWeight: 800, lineHeight: 1.2, mb: 1.5, color: '#ffffff' }}>
              Improve Your Search & AI Discoverability
            </Typography>

            <Typography variant="body1" sx={{ color: '#d8eaff', lineHeight: 1.6, mb: 2.3 }}>
              Manage technical SEO, indexing, analytics and AI-search readiness from one workspace.
            </Typography>

            <Stack spacing={1.05} sx={{ mb: 2.4 }}>
              {[
                'Technical SEO monitoring',
                'Search Console integration',
                'Analytics and reporting',
                'Automation and alerts',
              ].map((item) => (
                <Stack key={item} direction="row" spacing={1} alignItems="center">
                  <CheckCircleRoundedIcon sx={{ color: '#98d7ff', fontSize: 18 }} />
                  <Typography sx={{ color: '#d9ecff', fontWeight: 600 }}>{item}</Typography>
                </Stack>
              ))}
            </Stack>

            <Box
              sx={{
                mt: 1,
                borderRadius: 2,
                p: 2,
                background: 'linear-gradient(140deg, rgba(128, 188, 255, 0.18), rgba(128, 188, 255, 0.05))',
                border: '1px solid rgba(170, 216, 255, 0.22)',
              }}
            >
              <Stack spacing={0.7} alignItems="center">
                <Typography variant="body2" sx={{ color: '#d8ecff', fontWeight: 700 }}>Website</Typography>
                <Typography variant="caption" sx={{ color: '#acd4ff' }}>↓</Typography>
                <Typography variant="body2" sx={{ color: '#d8ecff', fontWeight: 700 }}>SEO signals</Typography>
                <Typography variant="caption" sx={{ color: '#acd4ff' }}>↓</Typography>
                <Stack direction="row" spacing={0.8} alignItems="center">
                  <TrendingUpRoundedIcon sx={{ color: '#9cd6ff', fontSize: 18 }} />
                  <Typography variant="body2" sx={{ color: '#d8ecff', fontWeight: 700 }}>
                    Search and AI visibility
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            <Typography variant="body2" sx={{ mt: 'auto', pt: 2.2, color: '#d7e9ff' }}>
              Secure, reliable and built for modern teams.
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={7} lg={7}>
          <Box
            sx={{
              minHeight: { xs: 'auto', md: 760 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: { xs: 0, sm: 1.5, md: 3.4, lg: 4.2 },
              py: { xs: 0, sm: 1.5, md: 2 },
              backgroundColor: '#f6f8fc',
            }}
          >
            <Box sx={{ width: '100%', maxWidth: 452 }}>
              <Stack direction="row" spacing={1.1} alignItems="center" sx={{ display: { xs: 'flex', md: 'none' }, mb: 1.6 }}>
                <Box
                  component="img"
                  src={brandLogoSrc}
                  alt="Asneris"
                  sx={{ width: 26, height: 26, objectFit: 'contain' }}
                />
                <Stack spacing={0.1}>
                  <Typography sx={{ color: '#173f79', fontWeight: 800, lineHeight: 1 }}>Asneris</Typography>
                  <Typography variant="caption" sx={{ color: '#5f7ea8', fontWeight: 700 }}>SEO / GEO Engine</Typography>
                </Stack>
              </Stack>

              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  p: { xs: 2.6, sm: 3.2, md: 4 },
                  borderRadius: 2,
                  border: '1px solid #dbe4ef',
                  boxShadow: '0 20px 50px rgba(6, 41, 95, 0.10), 0 4px 12px rgba(15, 23, 42, 0.05)',
                  backgroundColor: '#ffffff',
                }}
              >
                <Typography
                  component="h1"
                  variant="h4"
                  sx={{ fontWeight: 800, color: '#142f57', letterSpacing: '-0.02em', fontSize: { xs: '1.9rem', sm: '2rem' }, textAlign: 'center' }}
                  gutterBottom
                >
                  Forgot Password
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 2.5, sm: 3.2 }, textAlign: 'center' }}>
                  Enter your email address and we&apos;ll send you a temporary password. Use it to log in, then you&apos;ll be prompted to set a new password.
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                {success && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {success}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="Email address"
                    name="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailRoundedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{
                      mt: 3,
                      mb: 1.4,
                      height: 52,
                      borderRadius: 1.3,
                      background: 'linear-gradient(90deg, #164e91 0%, #1c73ba 100%)',
                      boxShadow: '0 10px 24px rgba(22, 78, 145, 0.24)',
                      fontWeight: 800,
                      '&:hover': {
                        background: 'linear-gradient(90deg, #124583 0%, #1969ac 100%)',
                        boxShadow: '0 14px 28px rgba(22, 78, 145, 0.34)',
                      },
                    }}
                    size="large"
                    disabled={loading}
                    endIcon={!loading ? <ArrowForwardRoundedIcon /> : undefined}
                  >
                    {loading ? 'Sending…' : 'Send Temporary Password'}
                  </Button>

                  <Typography variant="body2" align="center" sx={{ mt: 1.6 }}>
                    <Link component="button" variant="body2" onClick={() => navigate('/login')}>
                      Back to Sign In
                    </Link>
                  </Typography>
                </Box>
              </Paper>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1.5 }}>
                Need help? Contact support@asneris.com
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
